/* CrossDivisionStrip — drives organic circulation between the 3 ZHELTA
 * divisions and the homepage. Each page hides its own division card so a
 * Systems visitor only sees Services + Academy + Home, etc.
 *
 * Click events fire `/api/analytics/track-event` with `kind=cross_division_click`
 * (gated by cookie consent) so the admin can see which division feeds which.
 *
 * Visual: 3 dark-tan cards on a soft cream band, brand-coloured icon, single
 * line CTA. Designed to slot above the footer on every division page. */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Server, ShoppingBag, GraduationCap, Home as HomeIcon } from 'lucide-react';
import { getCookieConsent } from './CookieBanner';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const ALL_TILES = {
  home: {
    id: 'home',
    icon: HomeIcon,
    label: 'About ZHELTA',
    title: 'Who we are',
    body: 'One brand, three divisions — discover the story behind ZHELTA and the team that builds it.',
    cta: 'About ZHELTA',
    to: '/about',
  },
  systems: {
    id: 'systems',
    icon: Server,
    label: 'ZHELTA Systems',
    title: 'Enterprise IT & deployment',
    body: 'Managed IT, cybersecurity, cloud, and infrastructure for growing businesses.',
    cta: 'Explore Systems',
    to: '/systems',
  },
  services: {
    id: 'services',
    icon: ShoppingBag,
    label: 'ZHELTA Services',
    title: 'Verified hardware & gear',
    body: 'Laptops, peripherals, networking — vetted, priced, and shipped from one shop.',
    cta: 'Browse Services',
    to: '/services',
  },
  academy: {
    id: 'academy',
    icon: GraduationCap,
    label: 'ZHELTA Academy',
    title: 'Practical IT education',
    body: 'IT certification tracks, cybersecurity, programming, and life-skill programs.',
    cta: 'See Programs',
    to: '/academy',
  },
};

const ORDER = ['systems', 'services', 'academy', 'home'];

export default function CrossDivisionStrip({ hide = [] }) {
  const tiles = ORDER.filter((id) => !hide.includes(id)).map((id) => ALL_TILES[id]);

  const trackClick = (id) => {
    // Same consent gate as the analytics beacon. Silent on rejection so we
    // never store a page-view tied to an opted-out visitor.
    if (getCookieConsent()?.choice !== 'accepted') return;
    fetch(`${API}/analytics/track-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'cross_division_click',
        from_path: window.location.pathname,
        to_division: id,
      }),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  };

  return (
    <section
      className="border-y border-gray-200 bg-[#FAF6EC]"
      data-testid="cross-division-strip"
    >
      <div className="zh-container py-14 md:py-16">
        <div className="text-center mb-10">
          <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.22em] text-zinc-700">
            Three divisions, one ZHELTA
          </span>
          <h2 className="mt-3 text-2xl md:text-[28px] lg:text-[32px] font-extrabold text-gray-900 leading-tight">
            More from <span className="text-zinc-700">ZHELTA</span>
          </h2>
          <p className="mt-3 text-[14px] text-gray-600 max-w-[520px] mx-auto leading-relaxed">
            One brand, three integrated divisions — explore what you haven't yet.
          </p>
        </div>

        <div
          className={`grid gap-4 md:gap-5 ${tiles.length === 3 ? 'md:grid-cols-3' : tiles.length === 2 ? 'md:grid-cols-2 max-w-[760px] mx-auto' : 'md:grid-cols-1 max-w-[520px] mx-auto'}`}
        >
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.id}
                to={t.to}
                onClick={() => trackClick(t.id)}
                data-testid={`cross-division-tile-${t.id}`}
                className="group rounded-2xl border border-gray-200 bg-white p-6 hover:border-[#f5a623] hover:shadow-[0_8px_24px_-12px_rgba(245,166,35,0.4)] transition-all duration-200"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FEFBF3] text-zinc-700 inline-flex items-center justify-center flex-shrink-0 group-hover:bg-[#f5a623] group-hover:text-black transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] font-extrabold text-gray-500">
                      {t.label}
                    </div>
                    <div className="text-[16px] font-extrabold text-gray-900 leading-tight mt-0.5">
                      {t.title}
                    </div>
                  </div>
                </div>
                <p className="text-[13.5px] text-gray-600 leading-relaxed mb-4">{t.body}</p>
                <div className="inline-flex items-center gap-1 text-[12.5px] font-bold uppercase tracking-wider text-zinc-700 group-hover:text-zinc-700">
                  {t.cta}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
